import { useState } from 'react';
import type { KeyboardEvent } from 'react';
import type { DragEvent as ReactDragEvent } from 'react';
import {
  Download,
  Eye,
  EyeOff,
  ShoppingCart,
  Sparkles,
  Trash2
} from 'lucide-react';
import { RootState } from '../../../../store/store';
import { useDispatch, useSelector } from 'react-redux';
import { toggleImageAsset, toggleHide } from '../../slice/ImageAssetSlice';
import { Checkbox } from '@/components/ui/checkbox';
import { useImageLoaded } from '@/components/ui/hooks/use-image-loaded';
import { Typography } from '@/components/ui/typography';
import { cn } from '@/lib/utils';
import { canUseEditSuite } from '../../assetFeatureFlags';
import { persistAssetPatch } from '../../edit/assetEditSeams';
import CaptionEditor from '../../edit/CaptionEditor';
import AssetImageDetails from './AssetImageDetails';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip';
import { showAlert } from '@/slice/AlertSlice';
import { Variants } from '@/types/common';

type CardDragProps = {
  onDragStart: (photoUUID: string, e: ReactDragEvent<HTMLDivElement>) => void;
  onDragOver: (photoUUID: string, e: ReactDragEvent<HTMLDivElement>) => void;
  onDrop: (photoUUID: string, e: ReactDragEvent<HTMLDivElement>) => void;
  onDragEnd: () => void;
};

type AssetsImageCardProps = {
  photoUUID: string;
  index: number;
  inCart: boolean;
  onImageClick: (index: number) => void;
  nextPhotoUUID?: string;
  prevPhotoUUID?: string;
  dragProps?: CardDragProps;
  dropIndicator?: 'before' | 'after' | null;
  onStage?: (photoUUID: string) => void;
  onDownload?: (photoUUID: string) => void;
  onDelete?: (photoUUID: string) => void;
  /** 'staged' = Virtual Staging tab presentation (DV-2740): hidden photos
   * stay in the grid dimmed with a hover reveal and a "Hidden in Photos Tab"
   * flag, hover controls reduce to Download (available to everyone), and
   * hidden cards drop their checkbox — hidden state is view-only there. */
  variant?: 'photos' | 'staged';
};

const AssetsImageCard = ({
  photoUUID,
  index,
  inCart,
  onImageClick,
  nextPhotoUUID,
  prevPhotoUUID,
  dragProps,
  dropIndicator,
  onStage,
  onDownload,
  onDelete,
  variant = 'photos'
}: AssetsImageCardProps) => {
  const dispatch = useDispatch();
  const imageAsset = useSelector(
    (state: RootState) => state.imageAsset.imageAssets
  );
  const showImageDetails = useSelector(
    (state: RootState) => state.imageAsset.showImageDetails
  );
  const image = imageAsset.find((img) => img.photoUUID === photoUUID);
  const {
    loaded: imageLoaded,
    imageRef,
    markLoaded
  } = useImageLoaded(image?.caches?.['300w'] || image?.caches?.['830w']);
  const editEnabled = canUseEditSuite();
  const stagedVariant = variant === 'staged';
  // Dragging is disabled while the caption input has focus, so a drag
  // gesture started from inside the text field doesn't hijack a text
  // selection/cursor move. Belt-and-braces with the onDragStart guard below.
  const [captionFocused, setCaptionFocused] = useState(false);

  const handleImageKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    e.preventDefault();
    onImageClick(index);
  };

  // The top-left controls host the checkbox; stop propagation so clicks/keystrokes
  // there don't bubble to the image area and open the viewer modal.
  const stopBubble = (e: { stopPropagation: () => void }) =>
    e.stopPropagation();

  if (!image) {
    return null;
  }

  const handleToggleHide = () => {
    dispatch(toggleHide(photoUUID));
    persistAssetPatch({
      assetUUID: image.assetUUID,
      hidden: !image.hidden
    }).catch(() => {
      dispatch(toggleHide(photoUUID));
      dispatch(
        showAlert({
          type: Variants.ERROR,
          message: 'Could not update photo visibility — please try again.'
        })
      );
    });
  };

  const handleDownload = (e: { stopPropagation: () => void }) => {
    e.stopPropagation();
    onDownload?.(photoUUID);
  };

  const handleDelete = (e: { stopPropagation: () => void }) => {
    e.stopPropagation();
    onDelete?.(photoUUID);
  };

  return (
    <div
      draggable={Boolean(dragProps) && !captionFocused}
      onDragStart={
        dragProps
          ? (e) => {
              // Belt-and-braces alongside the captionFocused draggable toggle:
              // catch a drag gesture that starts from inside the caption input
              // before its focus state has committed a re-render.
              if ((e.target as HTMLElement).closest('[data-caption-input]')) {
                e.preventDefault();
                return;
              }
              dragProps.onDragStart(photoUUID, e);
            }
          : undefined
      }
      onDragOver={
        dragProps ? (e) => dragProps.onDragOver(photoUUID, e) : undefined
      }
      onDrop={dragProps ? (e) => dragProps.onDrop(photoUUID, e) : undefined}
      onDragEnd={dragProps ? () => dragProps.onDragEnd() : undefined}
      className="group relative rounded-lg ring-1 ring-border/40 overflow-hidden transition-all duration-200 hover:shadow-md hover:-translate-y-0.5 active:translate-y-0"
    >
      {dropIndicator && (
        <span
          aria-hidden
          className={cn(
            'absolute inset-y-0 z-10 w-1 rounded bg-primary',
            dropIndicator === 'before' ? 'left-0' : 'right-0'
          )}
        />
      )}
      {/* `bg-muted` is what the card shows before its photo paints: an
          intentional surface rather than a hole, so a grid mid-load reads as
          composed instead of broken. The image fades in over it (below). */}
      <div
        className="cursor-pointer block w-full aspect-[3/2] relative bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary"
        role="button"
        tabIndex={0}
        aria-label={`View full screen: ${image.caption ?? 'Image'}`}
        onClick={() => onImageClick(index)}
        onKeyDown={handleImageKeyDown}
      >
        <img
          ref={imageRef}
          src={image.caches?.['300w'] || image.caches?.['830w']}
          srcSet={`${image.caches?.['300w']} 300w, ${image.caches?.['830w']} 830w`}
          sizes="(max-width: 600px) 50vw, (max-width: 1280px) 25vw, 20vw"
          alt={image.caption ?? ''}
          loading={index < 5 ? 'eager' : 'lazy'}
          fetchPriority={index < 5 ? 'high' : 'auto'}
          decoding="async"
          onLoad={markLoaded}
          onError={markLoaded}
          className={cn(
            'block w-full h-full object-cover',
            // Photos arrive whenever the network delivers them; fading each one
            // in over the muted surface turns a ragged series of pop-ins into a
            // series of soft arrivals. Cached images skip it (see
            // useImageLoaded) so a filter toggle or reorder doesn't re-flash.
            'transition-opacity duration-300 motion-reduce:transition-none',
            imageLoaded ? 'opacity-100' : 'opacity-0',
            image.hidden && 'grayscale brightness-[.70]',
            image.hidden &&
              stagedVariant &&
              // Same fade, but this variant also animates filter on hover — one
              // transition declaration has to cover both properties.
              'transition-[filter,opacity] duration-200 group-hover:grayscale-0 group-hover:brightness-100'
          )}
        />

        {/* Hidden photos get a centered flag instead of the corner pill, so
            it reads clearly regardless of edit-suite gating. */}
        {image.hidden && (
          <div
            className={cn(
              'pointer-events-none absolute left-1/2 top-1/2 z-10 flex -translate-x-1/2 -translate-y-1/2 items-center gap-1.5 rounded-md bg-black/70 px-3.5 py-1.5 text-xs font-semibold tracking-wide text-white',
              stagedVariant &&
                'whitespace-nowrap transition-opacity duration-200 group-hover:opacity-0'
            )}
          >
            <EyeOff
              className={cn('h-3.5 w-3.5', stagedVariant && 'shrink-0')}
              aria-hidden
            />
            {stagedVariant ? 'Hidden in Photos Tab' : 'Hidden'}
          </div>
        )}

        {/* Top-left controls: checkbox + optional In Cart badge. No gradient —
            the card already carries a bottom gradient for the caption.

            Sub-lg positioning (Steffon, QA round 4 follow-up): the 44px tap
            root centers its painted box, which floated it ~20px into the
            card. The wrapper drops its padding and the box top-left-aligns
            inside the tap root with 6px padding (the design's inset) — the
            44px hit area now extends right/down over the image instead of
            pushing the visual inward. Desktop keeps the centered box + p-2. */}
        <div
          className="absolute top-0 left-0 flex items-center gap-2 p-2 max-lg:p-0"
          onClick={stopBubble}
          onKeyDown={stopBubble}
        >
          {!(stagedVariant && image.hidden) && (
            <Checkbox
              id={photoUUID}
              aria-label={`Select ${image.caption ?? 'image'}`}
              checked={image.isChecked ?? false}
              onCheckedChange={() => dispatch(toggleImageAsset(photoUUID))}
              className="max-lg:place-content-start max-lg:p-1.5"
              boxClassName="h-5 w-5 border-2 border-white bg-black/35 drop-shadow-[1px_1px_3px_var(--color-shadow-text-on-image)] [&_svg]:stroke-[2.5] group-data-[state=checked]/checkbox:bg-black/60 group-data-[state=checked]/checkbox:text-white"
            />
          )}
          {inCart && (
            <span className="pointer-events-none flex items-center gap-1 rounded-full bg-success px-2 py-0.5 text-[11px] font-medium text-white shadow-sm">
              <ShoppingCart className="h-3 w-3" aria-hidden />
              In Cart
            </span>
          )}
        </div>

        {/* Top-right: hover-revealed sparkle (stage) + eye toggle + download button. Sibling of
            the top-left controls; also stops propagation so it doesn't open
            the viewer modal.

            Each button pairs opacity-0 with pointer-events-none when idle:
            opacity alone leaves an invisible button hit-testable, so on a
            touch device (no hover) a corner tap silently fired hide/delete
            instead of opening the viewer (Steffon, QA round 4). The WRAPPER
            is pointer-events-none too — its own box (padding + the gaps
            between buttons) was still catching corner taps and its stopBubble
            swallowed them before the image's open-viewer click could fire
            (Steffon, follow-up). Buttons re-enable themselves via
            group-hover / focus-visible, and their clicks still bubble to this
            wrapper's stopBubble, so mouse and keyboard behavior is unchanged.

            `pointer-coarse:hidden` (Unit 4 PR B, spec §2.5 correction
            2026-08-21): on a touch-primary device the cluster isn't rendered
            at all. The opacity/pointer-events pairing above already made it
            inert there, but an inert 28x28 button is still a real box the
            tap-target gate measures — and teaching the gate to skip
            pointer-events:none elements would silence it about genuinely
            unreachable controls, which is the failure it exists to catch.
            Nothing is lost: every action here (stage / hide / download /
            delete) is in AssetsSelectionBar under the same capability gates,
            one checkbox tap away. Gated on pointer capability rather than
            width on purpose — a 1024px landscape iPad reads as "desktop" by
            width but has no hover. Scoped to the photos variant: the staged
            tab has no selection bar to fall back on (internal-only surface,
            banked for Unit 5), so its cards keep the hover cluster and the
            inert-idle treatment stays their safety net. */}
        <TooltipProvider>
          <div
            className={cn(
              'pointer-events-none absolute top-0 right-0 flex items-center gap-2 p-2',
              !stagedVariant && 'pointer-coarse:hidden'
            )}
            onClick={stopBubble}
            onKeyDown={stopBubble}
          >
            {/* On a photo the AI-staging commit flow actually produced, the
                sparkle reopens the staging modal in restage mode (DV-2252
                regenerate-after-staged) — the result replaces the committed
                photo in place. Keyed on `stagedFromAssetUUID`, NOT `staged`:
                `staged` also covers a plain upload filed into the Virtual
                Staging tab, which has no render behind it to regenerate
                (DV-2826). */}
            {onStage && (
              <Tooltip delayDuration={250}>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    aria-label={
                      image.stagedFromAssetUUID
                        ? `Regenerate ${image.caption ?? 'photo'} with AI`
                        : `Stage ${image.caption ?? 'photo'} with AI`
                    }
                    onClick={(e) => {
                      e.stopPropagation();
                      onStage(photoUUID);
                    }}
                    className="cursor-pointer rounded-full bg-black/45 p-1.5 text-white opacity-0 pointer-events-none transition-opacity group-hover:opacity-100 group-hover:pointer-events-auto focus-visible:opacity-100 focus-visible:pointer-events-auto"
                  >
                    <Sparkles className="h-4 w-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  {image.stagedFromAssetUUID
                    ? 'Regenerate with AI'
                    : 'Stage with AI'}
                </TooltipContent>
              </Tooltip>
            )}
            {!stagedVariant && editEnabled && (
              <Tooltip delayDuration={250}>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    aria-label={`${image.hidden ? 'Show' : 'Hide'} ${
                      image.caption ?? 'photo'
                    }`}
                    onClick={handleToggleHide}
                    className="cursor-pointer rounded-full bg-black/45 p-1.5 text-white opacity-0 pointer-events-none transition-opacity group-hover:opacity-100 group-hover:pointer-events-auto focus-visible:opacity-100 focus-visible:pointer-events-auto"
                  >
                    {image.hidden ? (
                      <Eye className="h-4 w-4" />
                    ) : (
                      <EyeOff className="h-4 w-4" />
                    )}
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  {image.hidden ? 'Show Photo' : 'Hide Photo'}
                </TooltipContent>
              </Tooltip>
            )}
            {(stagedVariant || editEnabled) && onDownload && (
              <Tooltip delayDuration={250}>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    aria-label={`Download ${image.caption ?? 'photo'}`}
                    onClick={handleDownload}
                    className="cursor-pointer rounded-full bg-black/45 p-1.5 text-white opacity-0 pointer-events-none transition-opacity group-hover:opacity-100 group-hover:pointer-events-auto focus-visible:opacity-100 focus-visible:pointer-events-auto"
                  >
                    <Download className="h-4 w-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom">Download Photo</TooltipContent>
              </Tooltip>
            )}
            {/* DV-2524: internal-only delete. The page supplies onDelete only
                when canDeleteAssets() AND a photoset is in context — the
                endpoints scope by photoset, so the order-only path has no
                affordance. Destructive tint distinguishes it from its
                neighbours at a glance. */}
            {onDelete && (
              <Tooltip delayDuration={250}>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    aria-label={`Delete ${image.caption ?? 'photo'}`}
                    onClick={handleDelete}
                    className="cursor-pointer rounded-full bg-black/45 p-1.5 text-white opacity-0 pointer-events-none transition-opacity hover:bg-destructive group-hover:opacity-100 group-hover:pointer-events-auto focus-visible:opacity-100 focus-visible:pointer-events-auto"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom">Delete Photo</TooltipContent>
              </Tooltip>
            )}
          </div>
        </TooltipProvider>

        {/* Caption stays bottom-left. */}
        <div className="flex items-center absolute bottom-0 left-0 w-full h-9 px-2 pt-2 pb-1.5 bg-gradient-to-r from-black/60 to-transparent">
          {editEnabled && (
            <span
              aria-label={`Photo ${index + 1}`}
              className="mr-1.5 shrink-0 text-[11px] font-semibold text-white/60"
            >
              {index + 1}
            </span>
          )}
          {editEnabled ? (
            <div
              className="flex min-w-0 flex-1"
              onClick={stopBubble}
              onKeyDown={stopBubble}
              onFocus={() => setCaptionFocused(true)}
              onBlur={() => setCaptionFocused(false)}
            >
              <CaptionEditor
                photoUUID={photoUUID}
                assetUUID={image.assetUUID}
                caption={image.caption ?? ''}
                nextPhotoUUID={nextPhotoUUID}
                prevPhotoUUID={prevPhotoUUID}
              />
            </div>
          ) : (
            // min-w-0 truncate: same clip the edit-mode measurement span
            // gets — a long caption must not widen the card at the phone
            // grid's two-up card width.
            <Typography
              variant="heading-md"
              className="min-w-0 truncate font-medium px-2 text-white leading-6 drop-shadow-[1px_1px_3px_var(--color-shadow-text-on-image)]"
            >
              {image.caption ?? ''}
            </Typography>
          )}
        </div>
      </div>
      {/* DV-2856: inside the card's ring, so the row reads as part of the card
          rather than floating under it. The design prototype pulls an
          equivalent row up with a negative margin because its card has no
          wrapper ring; ours does, so the honest layout gets the same result. */}
      {showImageDetails && (
        <AssetImageDetails imageID={image.imageID} createdAt={image.createdAt} />
      )}
    </div>
  );
};

export default AssetsImageCard;
